import { validateCourseUrls } from "./coursePayload";

export const importFields = [
  ["courseName", "Name", ["name", "title", "coursename", "coursetitle"], true],
  ["coursePrice", "Price", ["price", "courseprice", "cost"], true],
  [
    "levelName",
    "Academic Level",
    ["academiclevel", "grade", "gradelevel", "level"],
  ],
  ["storeName", "Store", ["store", "storename", "country"]],
  [
    "folderName",
    "Folder",
    [
      "folder",
      "foldername",
      "folderpath",
      "path",
      "parentfolder",
      "parentfoldername",
    ],
  ],
  [
    "subfolderName",
    "Subfolder",
    [
      "subfolder",
      "subfoldername",
      "childfolder",
      "folderchild",
      "foldersubfolder",
    ],
  ],
  ["courseDiscountAmount", "Discount Amount", ["discountamount"]],
  [
    "discountPercentage",
    "Discount Percentage",
    ["discountpercentage", "discountpercent"],
  ],
  ["courseImageUrl", "Image URL", ["imageurl", "courseimageurl", "coverurl"]],
  ["practiceUrl", "Practice URL", ["practiceurl"]],
  ["reviewMyMistakesUrl", "Review My Mistakes URL", ["reviewmymistakesurl"]],
  ["myPerformanceUrl", "My Performance URL", ["myperformanceurl"]],
];
export const normalizeName = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase();
const normalizeHeader = (value) => normalizeName(value).replace(/[\s_-]/g, "");
const normalizePath = (value) =>
  String(value ?? "")
    .replace(/\\/g, "/")
    .split(/\s*\/\s*/)
    .map((part) => normalizeName(part))
    .filter(Boolean)
    .join("/");

export function flattenImportFolders(nodes, parent = "", depth = 0) {
  return nodes.flatMap((node) => {
    const path = parent ? `${parent} / ${node.name}` : node.name;
    return [
      { ...node, path, parentPath: parent, depth },
      ...flattenImportFolders(node.subFolders || [], path, depth + 1),
    ];
  });
}
export function importSubfolders(options, folderId) {
  const parent = options.find((option) => String(option.id) === String(folderId));
  if (!parent) return [];
  const prefix = normalizePath(parent.path || parent.name) + "/";
  return options.filter((option) => normalizePath(option.path || option.name).startsWith(prefix));
}
export function inferColumns(headers) {
  return Object.fromEntries(
    importFields.map(([key, , aliases]) => [
      key,
      headers.findIndex((header) => aliases.includes(normalizeHeader(header))),
    ]),
  );
}
export function readImportSheet(XLSX, sheet) {
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: true,
    blankrows: true,
  });
  const header = matrix.findIndex((row) =>
    row.some((cell) => String(cell).trim()),
  );
  if (header < 0) throw new Error("This worksheet is empty.");
  const start = XLSX.utils.decode_range(sheet["!ref"] || "A1").s;
  const full = XLSX.utils.decode_range(
    sheet["!fullref"] || sheet["!ref"] || "A1",
  );
  if (full.e.r - start.r - header > 1000)
    throw new Error(
      "Import up to 1,000 rows at a time. Split this worksheet into smaller files.",
    );
  const rows = matrix.slice(header + 1).map((cells, index) => ({
    cells,
    rowNumber: start.r + header + index + 2,
  }));
  if (!rows.some(({ cells }) => cells.some((cell) => String(cell).trim())))
    throw new Error("No courses found below the headers.");
  return {
    headers: matrix[header].map(String),
    rows,
    startColumn: start.c,
    sheet,
  };
}
export function mapImportRows(XLSX, data, mapping) {
  for (const [key, label, , required] of importFields) {
    if (required && !(mapping[key] >= 0))
      throw new Error(`Choose a column for ${label}.`);
  }
  const used = Object.values(mapping).filter((column) => column >= 0);
  if (new Set(used).size !== used.length)
    throw new Error("Each spreadsheet column can only be mapped once.");
  return data.rows
    .map(({ cells, rowNumber }) => {
      const values = Object.fromEntries(
        importFields.map(([key]) => {
          const column = mapping[key];
          const cell =
            column >= 0
              ? data.sheet[
                  XLSX.utils.encode_cell({
                    r: rowNumber - 1,
                    c: data.startColumn + column,
                  })
                ]
              : null;
          return [
            key,
            String(
              (/url$/i.test(key) && cell?.l?.Target) ||
                cells[column] ||
                (cells[column] === 0 ? 0 : ""),
            ).trim(),
          ];
        }),
      );
      return { ...values, rowNumber };
    })
    .filter((row) => importFields.some(([key]) => row[key] !== ""));
}

function matchName(value, options, fallback, path = false) {
  if (!normalizeName(value)) {
    const found = options.find(
      (option) => String(option.id) === String(fallback),
    );
    return { id: found?.id || "", reason: found ? "" : "Choose a value" };
  }
  const pathValue = path && /[\\/]/.test(String(value || ""));
  const matches = options.filter((option) =>
    pathValue
      ? normalizePath(option.path || option.name) === normalizePath(value)
      : normalizeName(option.name) === normalizeName(value),
  );
  return {
    id: matches.length === 1 ? matches[0].id : "",
    reason:
      matches.length > 1
        ? `Multiple matches for “${value}”`
        : matches.length
          ? ""
          : `No match for “${value}”`,
  };
}

function findFolderParentId(options, folder) {
  if (!folder?.parentPath) return folder?.id || 0;
  const parent = options.find(
    (option) =>
      normalizePath(option.path || option.name) ===
      normalizePath(folder.parentPath),
  );
  return parent?.id || folder.id;
}

function matchFolder(row, options, fallback = "") {
  const parentValue = normalizeName(row.folderName || "");
  const childValue = normalizeName(row.subfolderName || "");
  const fallbackValue = normalizeName(
    row.folderName || row.subfolderName || "",
  );
  const explicitParent = Boolean(parentValue && childValue);
  const fullPathValue = normalizePath(
    row.folderName || row.subfolderName || "",
  );
  const combinedPathValue = normalizePath(
    [row.folderName, row.subfolderName].filter(Boolean).join(" / "),
  );
  const directPathValue = /[\\/]/.test(
    String(row.folderName || row.subfolderName || ""),
  );

  if (!childValue && !parentValue) {
    const found = options.find(
      (option) => String(option.id) === String(fallback),
    );
    return {
      id: found?.id || "",
      courseFolder: found ? findFolderParentId(options, found) : 0,
      courseSubfolder: found && found.parentPath ? found.id : 0,
      reason: found ? "" : "Choose a value",
    };
  }

  const parents = explicitParent ? options.filter((option) =>
    directPathValue
      ? normalizePath(option.path || option.name) === fullPathValue
      : normalizeName(option.name) === parentValue,
  ) : [];
  const candidates = options.filter((option) => {
    const optionPath = normalizePath(option.path || option.name);
    if (explicitParent) {
      return parents.some((parent) => {
        const parentPath = normalizePath(parent.path || parent.name);
        return optionPath.startsWith(parentPath + "/") &&
          (normalizeName(option.name) === childValue ||
            optionPath === parentPath + "/" + normalizePath(row.subfolderName));
      });
    }
    if (directPathValue) return optionPath === fullPathValue;
    return normalizeName(option.name) === (childValue || fallbackValue) ||
      optionPath === combinedPathValue;
  });

  const chosen = candidates.length === 1 ? candidates[0] : null;
  if (chosen) {
    return {
      id: chosen.id,
      courseFolder: explicitParent && parents.length === 1 ? parents[0].id : findFolderParentId(options, chosen),
      courseSubfolder: chosen.parentPath ? chosen.id : 0,
      reason: "",
    };
  }

  return {
    id: "",
    courseFolder: parents.length === 1 ? parents[0].id : 0,
    courseSubfolder: 0,
    reason:
      candidates.length > 1
        ? `Multiple matches for “${row.subfolderName || row.folderName}”`
        : `No match for “${row.subfolderName || row.folderName}”`,
  };
}

export function resolveImportRow(row, catalog, defaults = {}) {
  const level = matchName(row.levelName, catalog.levels, defaults.courseGrade);
  const store = matchName(row.storeName, catalog.stores, defaults.courseStore);
  const folderOptions = catalog.folders[String(store.id)] || [];
  const fallbackFolder =
    String(store.id) === String(defaults.courseStore)
      ? defaults.courseSubfolder || defaults.courseFolder
      : "";
  const folder = matchFolder(
    {
      folderName: row.folderName,
      subfolderName: row.subfolderName,
    },
    folderOptions,
    fallbackFolder,
  );

  return {
    ...row,
    courseGrade: level.id,
    courseStore: store.id,
    courseFolder: folder.courseFolder || folder.id || 0,
    courseSubfolder: folder.courseSubfolder || 0,
    courseImageFile: null,
    courseDiscountAmount: row.courseDiscountAmount || "0",
    discountPercentage: row.discountPercentage || "0",
    matchReasons: {
      courseGrade: level.reason,
      courseStore: store.reason,
      courseFolder: store.id ? folder.reason : "Choose a store first",
    },
  };
}
export function importRowErrors(row, catalog) {
  const errors = {};
  if (!row.courseName.trim() || row.courseName.includes("/"))
    errors.courseName = "Enter a name without / characters.";
  if (
    row.coursePrice === "" ||
    !Number.isFinite(Number(row.coursePrice)) ||
    Number(row.coursePrice) < 0
  )
    errors.coursePrice = "Enter a valid price of zero or more.";
  const exists = (options, id) =>
    options.some((option) => String(option.id) === String(id));
  if (!exists(catalog.levels, row.courseGrade))
    errors.courseGrade =
      row.matchReasons?.courseGrade || "Choose an academic level.";
  if (!exists(catalog.stores, row.courseStore))
    errors.courseStore = row.matchReasons?.courseStore || "Choose a store.";
  if (row.matchReasons?.courseFolder) errors.courseFolder = row.matchReasons.courseFolder;
  const selectedFolderId = row.courseSubfolder || row.courseFolder;
  if (row.courseSubfolder && !exists(importSubfolders(catalog.folders[String(row.courseStore)] || [], row.courseFolder), row.courseSubfolder))
    errors.courseFolder = "Choose a subfolder inside the selected folder.";
  if (!exists(catalog.folders[String(row.courseStore)] || [], selectedFolderId))
    errors.courseFolder =
      row.matchReasons?.courseFolder || "Choose a folder in this store.";
  const amount = Number(row.courseDiscountAmount),
    percentage = Number(row.discountPercentage);
  if (
    !Number.isFinite(amount) ||
    amount < 0 ||
    amount > Number(row.coursePrice)
  )
    errors.courseDiscountAmount =
      "Discount amount must be between zero and the price.";
  if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100)
    errors.discountPercentage =
      "Discount percentage must be between zero and 100.";
  if (amount > 0 && percentage > 0)
    errors.discountPercentage = "Use either a discount amount or percentage.";
  Object.assign(errors, validateCourseUrls(row));
  if (row.courseImageUrl) {
    try {
      if (!["http:", "https:"].includes(new URL(row.courseImageUrl).protocol))
        throw new Error();
    } catch {
      errors.courseImageUrl = "Enter an http or https image URL.";
    }
  }
  return errors;
}

// A manual store correction can resolve a previously unmatched folder name too.
export function changeImportDestination(row, key, value, catalog) {
  const next = {
    ...row,
    [key]: value,
    matchReasons: { ...row.matchReasons, [key]: "" },
  };
  if (key === "courseStore") {
    const folder = matchFolder(
      {
        folderName: row.folderName,
        subfolderName: row.subfolderName,
      },
      catalog.folders[String(value)] || [],
      "",
    );
    next.courseFolder = folder.courseFolder || folder.id || 0;
    next.courseSubfolder = folder.courseSubfolder || 0;
    next.matchReasons.courseFolder = folder.reason;
  }
  if (key === "courseFolder") {
    const options = catalog.folders[String(row.courseStore)] || [];
    const selected = options.find((option) => String(option.id) === String(value));
    next.courseFolder = selected?.id || 0;
    next.courseSubfolder = 0;
  }
  return next;
}

export function suggestImportNames(value, options) {
  const probe = normalizeName(value);
  if (probe.length < 3 || probe.length > 120) return [];
  const distance = (left, right) => {
    let previous = Array.from(
      { length: right.length + 1 },
      (_, index) => index,
    );
    for (let i = 1; i <= left.length; i += 1) {
      const current = [i];
      for (let j = 1; j <= right.length; j += 1)
        current[j] = Math.min(
          current[j - 1] + 1,
          previous[j] + 1,
          previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
        );
      previous = current;
    }
    return previous[right.length];
  };
  return options
    .map((option) => {
      const name = normalizeName(
        value.includes("/") ? option.path || option.name : option.name,
      );
      if (name.length > 120) return { option, score: Infinity };
      return {
        option,
        score:
          name.includes(probe) || probe.includes(name)
            ? 0
            : distance(probe, name),
      };
    })
    .filter(({ score }) => score <= Math.min(2, Math.floor(probe.length / 3)))
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map(({ option }) => option);
}
